// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
/* eslint-disable */

import { Easing, EasingFunction } from './easing';
import { Tween } from './Tween';
import { InterpolationFunction, linearInterpolation } from './interpolations';

export enum TweenDataState {
  Pending,
  Running,
  WaitingToRepeat,
  Looped,
  Complete,
}

export class TweenData {
  private vStart: object = {};

  private vStartCache: object = {};

  public vEnd: object = {};

  public value = 0;

  public repeatCounter = 0;

  public inReverse = false;

  private vEndCache: object = {};

  private duration = 1000;

  private repeatDelay: number | number[] = 0;

  private repeatDelayIndex = 0;

  private repeatTotal = 0;

  private yoyo = false;

  private yoyoDelay = 0;

  private delay = 0;

  private dt = 0;

  private startTime: number | null = null;

  private percent = 0;

  private easingFunction: EasingFunction = Easing.Default;

  private interpolationFunction: InterpolationFunction = linearInterpolation;

  private isRunning = false;

  private isFrom = false;

  constructor(private readonly parent: Tween) {
    this.parent = parent;
  }

  public to(
    proporties: object,
    duration: number,
    ease: EasingFunction,
    delay: number,
    repeat: number,
    yoyo: boolean,
  ): this {
    this.vEnd = proporties;
    this.duration = duration;
    this.easingFunction = ease;
    this.delay = delay;
    this.repeatTotal = repeat;
    this.yoyo = yoyo;

    return this;
  }

  public from(
    proporties: object,
    duration: number,
    ease: EasingFunction,
    delay: number,
    repeat: number,
    yoyo: boolean,
  ): this {
    this.to(proporties, duration, ease, delay, repeat, yoyo);
    this.isFrom = true;
    return this;
  }

  public start(): this {
    // check game time
    this.startTime = this.parent.getCurrentTime() + this.delay;

    this.dt = this.parent.reverse ? this.duration : 0;
    this.isRunning = this.delay > 0;
    if (this.isFrom) {
      for (const property in this.vStartCache) {
        if (!this.vStartCache.hasOwnProperty(property)) continue;
        this.vStart[property] = this.vEndCache[property];
        this.vEnd[property] = this.vStartCache[property];
        this.parent.target[property] = this.vStart[property];
      }
    }

    this.value = 0;
    this.repeatCounter = this.repeatTotal;

    return this;
  }

  // generateData

  public update(time: number, deltaTime: number): TweenDataState {
    if (!this.isRunning) {
      if (this.startTime !== null && time >= this.startTime) {
        this.isRunning = true;
      } else {
        return TweenDataState.Pending;
      }
    } else {
      // is running, but is waiting to repeat
      if (this.startTime !== null && time < this.startTime) {
        return TweenDataState.WaitingToRepeat;
      }
    }

    if (this.parent.reverse) {
      this.dt -= deltaTime * this.parent.timeScale;
      this.dt = Math.max(this.dt, 0);
    } else {
      this.dt += deltaTime * this.parent.timeScale;
      this.dt = Math.min(this.dt, this.duration);
    }

    this.percent = this.dt / this.duration;
    this.value = this.easingFunction(this.percent);

    for (const property in this.vEnd) {
      if (!this.vEnd.hasOwnProperty(property)) continue;
      const start: number = this.vStart[property];
      const end: number = this.vEnd[property];

      // TODO: yoyo tween when start is array
      if (Array.isArray(end)) {
        this.parent.target[property] = this.interpolationFunction(end, this.value);
      } else {
        this.parent.target[property] = start + (end - start) * this.value;
      }
    }

    if ((!this.parent.reverse && this.percent === 1) || (this.parent.reverse && this.percent === 0)) {
      return this.repeat();
    }

    return TweenDataState.Running;
  }

  public repeat(): TweenDataState {
    if (this.yoyo) {
      if (this.inReverse && this.repeatCounter === 0) {
        for (const property in this.vStartCache) {
          if (!this.vStartCache.hasOwnProperty(property)) continue;
          this.vStart[property] = this.vStartCache[property];
          this.vEnd[property] = this.vEndCache[property];
        }

        this.inReverse = false;
        return TweenDataState.Complete;
      }

      this.inReverse = !this.inReverse;
    } else if (this.repeatCounter === 0) {
      return TweenDataState.Complete;
    }

    if (this.inReverse) {
      //  If inReverse we're going from vEnd to vStartCache
      for (const property in this.vStartCache) {
        if (!this.vStartCache.hasOwnProperty(property)) continue;

        if (Array.isArray(this.vEndCache[property])) {
          this.vStart[property] = this.vEndCache[this.vEndCache[property].length - 1];
          this.vEnd[property] = this.vEndCache[property].slice().reverse();
        } else {
          this.vStart[property] = this.vEndCache[property];
          this.vEnd[property] = this.vStartCache[property];
        }
      }
    } else {
      for (const property in this.vStartCache) {
        if (!this.vStartCache.hasOwnProperty(property)) continue;
        this.vStart[property] = this.vStartCache[property];
        this.vEnd[property] = this.vEndCache[property];
      }

      if (this.repeatCounter > 0) {
        this.repeatCounter--;
      }
    }

    // check game time
    this.startTime = this.parent.getCurrentTime();

    if (this.yoyo && this.inReverse) {
      this.startTime += this.yoyoDelay;
    } else if (!this.inReverse) {
      if (Array.isArray(this.repeatDelay)) {
        this.startTime += this.repeatDelay[this.repeatDelayIndex++ % this.repeatDelay.length];
      } else {
        this.startTime += this.repeatDelay;
      }
    }

    if (this.parent.reverse) {
      this.dt = this.duration;
    } else {
      this.dt = 0;
    }

    return TweenDataState.Looped;
  }

  public loadValues(): this {
    for (const property in this.parent.properties) {
      if (!this.parent.properties.hasOwnProperty(property)) continue;
      this.vStart[property] = this.parent.properties[property];

      if (Array.isArray(this.vEnd[property])) {
        if (this.vEnd[property].length === 0) {
          continue;
        }

        if (this.percent === 0) {
          //  Put the start value at the beginning of the array
          //  but we only want to do this once, if the Tween hasn't run before
          this.vEnd[property] = [this.vStart[property]].concat(this.vEnd[property]);
        }
      }

      if (typeof this.vEnd[property] !== undefined) {
        if (typeof this.vEnd[property] === 'string') {
          this.vEnd[property] = this.vStart[property] + parseFloat(this.vEnd[property]);
        }

        this.parent.properties[property] = this.vEnd[property];
      } else {
        // null tween
        this.vEnd[property] = this.vStart[property];
      }

      this.vStartCache[property] = this.vStart[property];
      this.vEndCache[property] = this.vEnd[property];
    }

    return this;
  }
}
